/** Frontend-API für die Founder-Inbox (compass-new Freigabe-Weg, Layer 4). */

/** Prompt-Verbesserungs-Vorschlag des Self-Improvement-Loops (an playbook_approval-Items angehängt). */
export interface PromptProposal {
  id: number;
  actionId: string;
  runId: string;
  playbookId: number;
  blockId: string;
  feedback: string;
  oldPrompt: string;
  newPrompt: string;
  status: 'pending' | 'applied' | 'discarded' | 'superseded';
  createdAt: string;
  resolvedAt: string | null;
}

export interface InboxAction {
  actionId: string;
  strategyId: string | null;
  kind: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'approved' | 'vetoed' | 'executed' | 'failed';
  autonomyLevel: number;
  result: Record<string, unknown> | null;
  /** Nur bei kind='playbook_approval': aktuell offener Prompt-Vorschlag (oder null). */
  promptProposal?: PromptProposal | null;
}

export interface TrustEntry {
  owner: string;
  actionType: string;
  context: string;
  autonomyLevel: number;
  nActions: number;
  nApproved: number;
  nVetoed: number;
  wilsonLower: number;
  cooldownUntilN: number;
}

export interface InboxResponse {
  items: InboxAction[];
  counts: Record<string, number>;
  trust: TrustEntry[];
}

const URL = '/api/founder-inbox';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function listInbox(status?: string): Promise<InboxResponse> {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return fetchJson<InboxResponse>(`${URL}${q}`);
}

export async function approveAction(actionId: string): Promise<InboxAction> {
  return fetchJson<InboxAction>(`${URL}/${encodeURIComponent(actionId)}/approve`, { method: 'POST' });
}

export async function vetoAction(actionId: string, harmful = false): Promise<InboxAction> {
  return fetchJson<InboxAction>(`${URL}/${encodeURIComponent(actionId)}/veto`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ harmful }),
  });
}

/** Self-Improvement-Loop: Feedback → Entwurf neu generieren + (best-effort) Prompt-Vorschlag. */
export async function regenerateWithFeedback(actionId: string, feedback: string): Promise<{ action: InboxAction; proposal: PromptProposal | null }> {
  return fetchJson<{ action: InboxAction; proposal: PromptProposal | null }>(`${URL}/${encodeURIComponent(actionId)}/regenerate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback }),
  });
}

/** Nur neu generieren — ohne Feedback, mit dem aktuellen (ggf. zwischenzeitlich verbesserten) Block-Prompt. */
export async function regenerateOnly(actionId: string): Promise<InboxAction> {
  return fetchJson<InboxAction>(`${URL}/${encodeURIComponent(actionId)}/regenerate-only`, { method: 'POST' });
}

/** Offenen Prompt-Vorschlag in die Playbook-Definition übernehmen (künftige Runs). */
export async function applyPromptProposal(actionId: string): Promise<PromptProposal> {
  return fetchJson<PromptProposal>(`${URL}/${encodeURIComponent(actionId)}/apply-prompt`, { method: 'POST' });
}

/** Offenen Prompt-Vorschlag verwerfen. */
export async function discardPromptProposal(actionId: string): Promise<{ ok: boolean }> {
  return fetchJson<{ ok: boolean }>(`${URL}/${encodeURIComponent(actionId)}/discard-prompt`, { method: 'POST' });
}
